// Chat Popout Page
// Standalone chat window that can be moved to any monitor

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, MessageCircle, ChevronDown, Sparkles, Building2, BookOpen, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useChatStore, useActiveConversation, useActiveMessages } from '@/stores/chatStore';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ConversationList } from './ConversationList';
import { SourcesPanel } from './SourcesPanel';
import { useAuth } from '@/contexts/useAuth';
import type { CitationMetadata } from '@/types/chat';
import { createComponentLogger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import { AGENT_CONFIGS, DEFAULT_AGENT, getAgentConfig } from '@/config/agents';
import type { AgentType } from '@/types/chat';

const log = createComponentLogger('ChatPopout', 'ai-chat');

// Icon mapping for agent selector
const AGENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Building2,
  BookOpen
};

// ============================================================================
// Main Component
// ============================================================================

export function ChatPopout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [showConversationList, setShowConversationList] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType>(DEFAULT_AGENT);
  const [showSourcesPanel, setShowSourcesPanel] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<CitationMetadata | null>(null);

  // Get conversation ID from URL if provided
  const urlConversationId = searchParams.get('conversation');

  // Zustand store hooks
  const {
    conversations,
    activeConversationId,
    isLoadingConversations,
    isSending,
    error,
    loadConversations,
    createConversation,
    setActiveConversation,
    sendMessageStreaming,
    regenerateMessage,
    clearError
  } = useChatStore();

  const activeConversation = useActiveConversation();
  const messages = useActiveMessages();

  // Filter conversations by selected agent type
  const filteredConversations = conversations.filter(
    (c) => c.agent_type === selectedAgentType
  );

  // Collect all citations from messages
  const allCitations = useMemo(() => {
    return messages
      .filter((m) => m.role === 'assistant' && m.metadata?.citations)
      .flatMap((m) => m.metadata!.citations as CitationMetadata[]);
  }, [messages]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Set window title
  useEffect(() => {
    document.title = 'Lendwise AI Chat';
  }, []);

  // Load conversations on mount
  useEffect(() => {
    if (user && conversations.length === 0) {
      log.info('Loading conversations in popout');
      loadConversations();
    }
  }, [user]);

  // Set active conversation from URL param and sync agent type
  useEffect(() => {
    if (urlConversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === urlConversationId);
      if (conv) {
        // Sync selectedAgentType to match the loaded conversation
        setSelectedAgentType(conv.agent_type);
        setActiveConversation(urlConversationId);
      }
    }
  }, [urlConversationId, conversations]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleNewConversation = async (agentType?: AgentType) => {
    const typeToUse = agentType || selectedAgentType;
    log.info('Creating new conversation', { agentType: typeToUse });
    try {
      const conversationId = await createConversation(typeToUse);
      setActiveConversation(conversationId);
      setShowConversationList(false);

      // Update URL with new conversation ID (using setSearchParams for React Router sync)
      setSearchParams({ conversation: conversationId });
    } catch (error) {
      log.error('Failed to create conversation', { agentType: typeToUse }, error as Error);
    }
  };

  const handleSendMessage = async (content: string, images?: File[]) => {
    let targetConversationId = activeConversationId;

    if (!targetConversationId) {
      targetConversationId = await createConversation(selectedAgentType);
      setActiveConversation(targetConversationId);
      setSearchParams({ conversation: targetConversationId });
    }

    log.info('Sending message', {
      conversationId: targetConversationId,
      contentLength: content.length,
      imageCount: images?.length || 0
    });

    await sendMessageStreaming(content, images, targetConversationId);
  };

  const handleSelectConversation = (conversationId: string) => {
    log.debug('Selecting conversation', { conversationId });
    setActiveConversation(conversationId);
    setShowConversationList(false);
    setSearchParams({ conversation: conversationId });
  };

  const handleClose = () => {
    window.close();
  };

  const handleSelectCitation = useCallback((citation: CitationMetadata) => {
    setSelectedCitation(citation);
    setShowSourcesPanel(true);

    // Go fullscreen when opening PDF
    if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {
        // Silently fail if fullscreen not allowed
      });
    }
  }, []);

  const handleCloseSourcesPanel = useCallback(() => {
    setShowSourcesPanel(false);

    // Exit fullscreen when closing PDF panel
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {
        // Silently fail
      });
    }
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  // Auth loading state
  if (authLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <p className="text-lg font-medium mb-2">Session Expired</p>
          <p className="text-muted-foreground text-sm mb-4">
            Please log in to the main application and try again.
          </p>
          <Button onClick={handleClose}>Close Window</Button>
        </div>
      </div>
    );
  }

  const agentConfig = activeConversation
    ? getAgentConfig(activeConversation.agent_type)
    : getAgentConfig(selectedAgentType);

  // Get the icon component for the current agent
  const AgentIcon = AGENT_ICONS[agentConfig.icon] || Sparkles;

  return (
    <div className="h-screen w-screen flex flex-col bg-background">
      {/* Header */}
      <div className="px-4 py-2 border-b border-border flex items-center justify-between shrink-0 bg-background">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 gap-2 px-2">
                <AgentIcon className="h-4 w-4" />
                <span className="font-semibold">{agentConfig.name}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(Object.keys(AGENT_CONFIGS) as AgentType[])
                .filter((key) => ['wisr', 'encompass_helper', 'investor_guidelines'].includes(key))
                .map((key) => {
                  const config = AGENT_CONFIGS[key];
                  const Icon = AGENT_ICONS[config.icon] || Sparkles;
                  return (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => {
                        setSelectedAgentType(key);
                        // Clear active conversation to show welcome screen for new agent
                        setActiveConversation(null);
                        // Clear URL param to prevent re-selection by URL effect
                        setSearchParams({});
                      }}
                      className="gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span>{config.name}</span>
                        <span className="text-xs text-muted-foreground">{config.description}</span>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowConversationList(!showConversationList)}
                  disabled={isLoadingConversations}
                >
                  <MessageCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{showConversationList ? 'Hide' : 'Show'} History</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {allCitations.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={showSourcesPanel ? 'secondary' : 'ghost'}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowSourcesPanel(!showSourcesPanel)}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{showSourcesPanel ? 'Hide' : 'Show'} Sources</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleNewConversation()}
                  disabled={isSending}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>New conversation</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Close</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Conversation List Sidebar - Collapsible */}
        {showConversationList && (
          <div className="w-60 border-r border-border bg-muted/5 flex flex-col shrink-0">
            <div className="px-3 py-2 border-b border-border/50 flex items-center justify-between">
              <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">History</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => handleNewConversation()}
                disabled={isSending}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <ConversationList
                conversations={filteredConversations}
                activeConversationId={activeConversationId}
                onSelect={handleSelectConversation}
                onNew={handleNewConversation}
              />
            </ScrollArea>
          </div>
        )}

        {/* Chat Area */}
        <div className={cn("flex flex-col", showSourcesPanel ? "w-1/2" : "flex-1")}>
          {/* Error Display */}
          {error && (
            <div className="px-6 py-3 bg-destructive/10 border-b border-destructive/20">
              <div className="flex items-center justify-between">
                <p className="text-sm text-destructive">{error.message}</p>
                <Button variant="ghost" size="sm" onClick={clearError}>
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          {/* Messages Area */}
          <ScrollArea className="flex-1 px-6">
            <MessageList
              conversationId={activeConversationId || ''}
              messages={messages}
              isLoading={isSending}
              onSelectCitation={handleSelectCitation}
              onRegenerate={regenerateMessage}
              allowRegenerate={activeConversation?.agent_type !== 'wisr'}
            />
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t border-border p-3">
            <MessageInput
              onSend={handleSendMessage}
              disabled={isSending}
              placeholder={
                activeConversationId
                  ? `Ask ${agentConfig.name}...`
                  : 'Start a new conversation...'
              }
            />
          </div>
        </div>

        {/* Sources Panel - Right side dock (50% width when open) */}
        {showSourcesPanel && allCitations.length > 0 && (
          <div className="w-1/2 border-l border-border">
            <SourcesPanel
              citations={allCitations}
              selectedCitation={selectedCitation}
              onSelectCitation={handleSelectCitation}
              onClose={handleCloseSourcesPanel}
            />
          </div>
        )}
      </div>
    </div>
  );
}
